import React, { useContext, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import "./About_Application_Page.css";
import { useTranslation } from "react-i18next";
import ChatBotComponent from '../components/ChatBotComponent';
import { motion } from 'framer-motion';
import { ClenseNumeral } from "../utils/PageDataPrepare"; 
import { getStatsPageCounters } from "../http/StatsPage_API";
import { fetchTesting_Counters } from "../http/Data_API/Testing_Data_API";
import { Context } from "..";

const About_Application_Page = observer(() => {
    const { user } = useContext(Context);
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [testingStats, setTestingStats] = useState(null);
    const statsRefs = useRef([]);
    const sectionRef = useRef();

    useEffect(() => {
        getStatsPageCounters().then(s => setStats(s));
        fetchTesting_Counters().then(data => {
            setTestingStats(data);
        });
    }, []);

    const StatItem = ({ count, translationKey, index }) => {
        const formatNumber = (num) => {
            const number = num ?? 0;
            if (number >= 1000) {
                return Math.ceil(number / 500) * 500;
            }
            return number;
        };

        const roundedCount = formatNumber(count);
        const isRounded = (count ?? 0) >= 1000;
        const wordForms = t(translationKey, { returnObjects: true });
    
        return (
            <motion.div 
                className="stat-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
            >
                <div className="stat-number" ref={el => (statsRefs.current[index] = el)}>
                    {isRounded ? `>${roundedCount.toLocaleString()}` : (count ?? 0).toLocaleString()}
                </div>
                <div className="stat-label">
                    {ClenseNumeral(isRounded ? roundedCount : (count ?? 0), wordForms)}
                </div>
            </motion.div>
        );
    };

    return (
        <div className="about_app_page">

            <section className="header-section">        
                <div className="header-content">
                <video className='background-video' autoPlay={true} loop={true} muted={true}>
                    <source src='/background_video/background_video_1.mp4' type='video/mp4' />
                </video>
                    <img src={t('logo_pic_about')} alt="Logo" className={ 'logo-pic-' + user.language } />
                    <h1 className="header-title">{t('bar_title_mini')}</h1>
                </div>
            </section>

            <section className="features-section">
                <div className="section-header">
                    <br />
                    <h2>{t('about_main_description')}<br /></h2>
                    <h3></h3>                
                </div>
                <div className="features-grid">
                    <div className="feature-card">
                    <div className="feature-icon">💉</div>
                    <h3>{t('feature_1')}</h3>
                    <p>{t('bar_title_mini_2')}</p>
                    </div>
                    <div className="feature-card">
                    <div className="feature-icon">🧬</div>
                    <h3>{t('feature_3')}</h3>
                    <p>{t('bar_title_mini_1')}</p>
                    </div>
                    <div className="feature-card">
                    <div className="feature-icon">🦠</div>
                    <h3>{t('feature_2')}</h3>
                    <p>{t('about_images_text')}</p>
                    </div>
                </div>
            </section>

            <section className="biology-section">
                <div className="biology-overlap-container">
                    <div className="biology-images-stack">

                        <motion.div 
                            className="biology-image-wrap image-back"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <img 
                                src='/icons/about/biology_screen_1.png' 
                                alt="Medical illustration" 
                                loading="lazy"
                            />
                        </motion.div>
                        
                        <motion.div 
                            className="biology-image-wrap image-middle"
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                        >
                            <img 
                                src='/icons/about/biology_screen_2.png' 
                                alt="Medical research" 
                                loading="lazy"
                            />
                        </motion.div>

                        <motion.div 
                            className="biology-image-wrap image-front"
                            initial={{ opacity: 0, x: -5 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.6 }}
                        >
                            <img 
                                src='/icons/about/biology_screen_3.png' 
                                alt="Medical data" 
                                loading="lazy"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            <section className="stats-section" ref={sectionRef}>
                <div className="stats-container">
                    {/* <StatItem 
                        count={stats?.universities} 
                        translationKey="stats.university" 
                        index={0}
                    />
                    <StatItem 
                        count={stats?.users} 
                        translationKey="stats.user" 
                        index={1}
                    /> */}
                    <StatItem 
                        count={stats?.totalCases} 
                        translationKey="stats.case" 
                        index={2}
                    />
                    <StatItem 
                        count={stats?.totalCategories} 
                        translationKey="stats.category" 
                        index={3}
                    />
                    <StatItem 
                    count={testingStats?.total} 
                    translationKey="stats.test" 
                    index={4}
                />
                {/* <StatItem 
                    count={testingStats?.categories?.length} 
                    translationKey="stats.test_categories" 
                    index={5}
                /> */}
                </div>
            </section>

            <section className="compact-section categories">
                <div className="section-header">

                    <h1>{t('selected_categories')}</h1>
                    
                </div>
                <div className="compact-categories">

                {stats?.categories?.map((category, index) => (
                    <motion.span
                    key={index}
                    className="category-tag"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{
                        delay: index * 0.08,
                        type: "spring",
                        stiffness: 150,
                        damping: 16
                    }}
                    viewport={{ once: true, amount: 0.4 }} 
                    >
                    {t(category.path + '_title')}
                    </motion.span>
                ))}
                </div>
            </section>


            <section className="testimonials-section">
                <div className="section-header">
                    <h2>{t('registry_text')}</h2>
                </div>
                <div className="testimonials-carousel">
                    {t('reviews', { returnObjects: true }).map((review, index) => (
                    <div className="testimonial-card" key={index}>
                        <div className="testimonial-content">
                        <p>"{review.quote}"</p>
                        </div>
                        <div className="testimonial-author">
                        <div className="author-avatar">{review.initials}</div>
                        <div className="author-info">
                            <div className="author-name">{review.name}</div>
                            <div className="author-title">{review.position}</div>
                        </div>
                        </div>
                    </div>
                    ))}
                </div>
            </section>

            <section className="cta-section">
                <div className="cta-container">
                    <h2>{t('contact_us_about')}</h2>
                   
                    <div className="cta-buttons">
                    <a href="mailto:sale@pimunn.net">
                        <button className="cta-button primary">{t('support_contact_button')}</button>
                        </a>
                    </div>
                </div>
            </section>

            <ChatBotComponent />
        </div>
    );
});

export default About_Application_Page;
